import { useState } from "react";
import { NavLink, Outlet, useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  LayoutDashboard, Users, ClipboardList, LineChart, BarChart3,
  IndianRupee, FolderKanban, Tags, Settings, UserCog, LogOut,
  ChevronLeft, ChevronRight, Circle, Layers, Menu, Zap, Shield, Package,
} from "lucide-react";
import { api } from "@/lib/api";
import { useAvailabilityTracker } from "@/hooks/useAvailabilityTracker";
import NotificationsBell from "@/components/app/NotificationsBell";

const PROFESSIONAL_ROUTES = new Set(["/clients", "/analytics"]);

const MANAGER_NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/tasks", label: "Management Tasks", icon: ClipboardList, testid: "nav-tasks" },
  { to: "/clients", label: "Clients", icon: Users, testid: "nav-clients" },
  { to: "/freelancers", label: "Freelancers", icon: Users, testid: "nav-freelancers" },
  { to: "/work-orders", label: "Work Orders", icon: ClipboardList, testid: "nav-work-orders" },
  { to: "/performance", label: "Performance", icon: LineChart, testid: "nav-performance" },
  { to: "/analytics", label: "Analytics", icon: BarChart3, testid: "nav-analytics" },
  { to: "/management-analytics", label: "Management Analytics", icon: Shield, testid: "nav-management-analytics", onlyRoles: ["SuperAdmin", "TeamLeader"] },
  { to: "/payments", label: "Payments", icon: IndianRupee, testid: "nav-payments" },
  { to: "/projects", label: "Projects", icon: FolderKanban, testid: "nav-projects" },
  { to: "/vendors", label: "Vendors", icon: Package, testid: "nav-vendors" },
  { to: "/services", label: "Services", icon: Layers, testid: "nav-services" },
  { to: "/automation-settings", label: "Automations", icon: Zap, testid: "nav-automations" },
  { to: "/rate-cards", label: "Rate Cards", icon: Tags, testid: "nav-pricing" },
  { to: "/settings", label: "Settings", icon: Settings, testid: "nav-settings" },
];

const SUPER_ADMIN_NAV = [
  ...MANAGER_NAV.slice(0, -1),
  { to: "/users", label: "Users", icon: UserCog, testid: "nav-users" },
  { to: "/settings", label: "Settings", icon: Settings, testid: "nav-settings" },
];

const TEAM_LEADER_NAV = SUPER_ADMIN_NAV;

const FREELANCER_NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/work-orders", label: "Work Orders", icon: ClipboardList, testid: "nav-work-orders" },
  { to: "/projects", label: "Projects", icon: FolderKanban, testid: "nav-projects" },
  { to: "/performance", label: "Performance", icon: LineChart, testid: "nav-performance" },
  { to: "/payments", label: "Payments", icon: IndianRupee, testid: "nav-payments" },
  { to: "/settings", label: "Settings", icon: Settings, testid: "nav-settings" },
];

export const AVAILABILITY_OPTIONS = ["Available Now", "Online", "Not Available", "Away for Few Hours", "On Leave", "In Meeting"];

export function availabilityTone(status) {
  switch (status) {
    case "Available Now": return "text-[#40916C]";
    case "Online": return "text-[#B8974A]";
    case "In Meeting": return "text-[#E5D7B4]";
    case "Away for Few Hours": return "text-[#FF8A00]";
    case "On Leave":
    case "Not Available": return "text-[#B02A00]";
    default: return "text-white/40";
  }
}

export default function Layout() {
  const { user, logout, refresh } = useAuth();
  const nav = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  useAvailabilityTracker(user, refresh);

  let items = FREELANCER_NAV;
  if (user?.role === "SuperAdmin") items = SUPER_ADMIN_NAV;
  else if (user?.role === "TeamLeader") items = TEAM_LEADER_NAV;
  else if (user?.role === "Manager") items = MANAGER_NAV;
  // Honour per-item role restriction
  items = items.filter((i) => !i.onlyRoles || i.onlyRoles.includes(user?.role));

  const changeAvailability = async (status) => {
    try { await api.patch("/auth/me/status", { availability_status: status }); await refresh(); } catch { /* silent */ }
  };

  return (
    <div className="min-h-screen flex items-start bg-cream">
      {mobileOpen && <div data-testid="sidebar-backdrop" className="fixed inset-0 z-40 bg-black/40 md:hidden" onClick={() => setMobileOpen(false)} />}
      <aside data-testid="sidebar" className={`bg-royal-800 text-cream flex flex-col shrink-0 fixed md:sticky inset-y-0 left-0 z-50 md:z-auto top-0 h-screen overflow-y-auto ${collapsed ? "md:w-16" : "md:w-60"} w-64 ${mobileOpen ? "translate-x-0" : "-translate-x-full"} md:translate-x-0 transition-[width,transform] duration-200`}>
        <div className="flex items-center justify-between px-4 py-5 border-b border-white/10">
          {!collapsed && (
            <Link to="/" className="font-display font-medium text-xl leading-none tracking-tight">FreLancTrac<span className="text-gold">.</span></Link>
          )}
          <button data-testid="sidebar-toggle" onClick={() => setCollapsed((c) => !c)} className="p-1.5 hover:bg-white/10 rounded-md hidden md:block" aria-label="Toggle sidebar">
            {collapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
          </button>
          <button data-testid="sidebar-close-mobile" onClick={() => setMobileOpen(false)} className="p-1.5 hover:bg-white/10 rounded-md md:hidden" aria-label="Close menu"><ChevronLeft size={16} /></button>
        </div>

        <nav className="flex-1 p-2 space-y-0.5">
          {items.map((it) => {
            const Icon = it.icon;
            return (
              <NavLink key={it.to} to={it.to} end={it.to === "/dashboard"} data-testid={it.testid} onClick={() => setMobileOpen(false)}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2 text-sm rounded-md transition-colors ${
                    isActive ? "bg-royal-600 text-white" : "text-cream/80 hover:bg-white/10 hover:text-white"
                  }`}>
                <Icon size={16} strokeWidth={1.75} />
                {!collapsed && <span>{it.label}</span>}
              </NavLink>
            );
          })}
        </nav>

        <div className="border-t border-white/10 p-3 space-y-3">
          {!collapsed && (
            <div>
              <div className="text-[10px] uppercase tracking-widest text-cream/50 font-mono-tight">{user?.role}</div>
              <div className="text-sm font-semibold truncate" data-testid="sidebar-user-name">{user?.name}</div>
              <div className="mt-3">
                <label className="text-[10px] uppercase tracking-widest text-cream/50 font-mono-tight block mb-1">My Status</label>
                <select data-testid="availability-select" value={user?.availability_status || "Online"} onChange={(e) => changeAvailability(e.target.value)}
                  className="w-full bg-royal-900 border border-white/15 rounded-md px-2 py-1 text-xs text-cream font-mono-tight">
                  {AVAILABILITY_OPTIONS.map((s) => <option key={s}>{s}</option>)}
                </select>
                <div className="flex items-center gap-1 mt-1 text-xs">
                  <Circle size={8} className={`fill-current ${availabilityTone(user?.availability_status)}`} />
                  <span className="text-cream/60 font-mono-tight">{user?.availability_status || "Online"}</span>
                </div>
              </div>
            </div>
          )}
          <button data-testid="logout-btn" onClick={async () => { await logout(); nav("/"); }}
            className="w-full flex items-center gap-2 justify-center border border-white/25 text-cream hover:bg-white hover:text-royal-800 text-sm py-1.5 rounded-full transition-colors">
            <LogOut size={14} />
            {!collapsed && <span>Sign out</span>}
          </button>
        </div>
      </aside>

      <main className="flex-1 min-w-0 bg-cream">
        <div className="sticky top-0 z-30 bg-cream/70 backdrop-blur-sm border-b border-black/5 flex justify-between md:justify-end items-center px-4 md:px-6 py-2">
          <button data-testid="mobile-menu-btn" onClick={() => setMobileOpen(true)} className="md:hidden p-2 rounded-md bg-royal-800 text-cream" aria-label="Open menu"><Menu size={16} /></button>
          <div className="bg-royal-800 rounded-full px-1 py-1"><NotificationsBell /></div>
        </div>
        <Outlet />
      </main>
    </div>
  );
}

export function PageHeader({ title, description, actions, testid }) {
  return (
    <div data-testid={testid || "page-header"} className="bg-white border-b border-black/5 px-4 md:px-8 py-6 md:py-8 flex flex-col sm:flex-row items-start justify-between gap-4">
      <div>
        <h1 className="font-display text-4xl md:text-5xl font-medium tracking-tight leading-none text-royal-800">{title}</h1>
        {description && <div className="text-sm text-neutral-600 mt-3 max-w-xl">{description}</div>}
      </div>
      {actions && <div className="flex gap-2 flex-wrap">{actions}</div>}
    </div>
  );
}
